import { ImageBackground, type ImageSourcePropType } from 'react-native';
import {
    BodyLargeRegular,
    BodyMediumRegular,
    H1Emphasized,
    HeroEmphasized,
    isNonMobileWeb,
    isWeb,
    SafeAreaView,
    ScrollView,
    useThemeColors,
    View,
    YStack,
} from 'smoothie';

import Logo from '../../../../assets/images/logos/logo-new.svg';

export const SplitScreenHighlightLayout = ({
    backgroundImage,
    title,
    subtitle,
    includeLogo = false,
    disclosures,
    scrollViewTestID,
    children,
}: {
    backgroundImage: ImageSourcePropType;
    title: string;
    subtitle: string;
    includeLogo?: boolean;
    disclosures?: React.ReactNode;
    /** Optional — identifies the scrollable column for web/E2E (RN ScrollView host). */
    scrollViewTestID?: string;
    children: React.ReactNode;
}) => {
    const { getColor } = useThemeColors();

    const mediaHeightNumber = 52;
    const mediaHeight = isNonMobileWeb ? '100%' : `${mediaHeightNumber}vh`;
    const contentTopMargin = isNonMobileWeb ? 0 : `${mediaHeightNumber - 12}vh`;

    return (
        <SafeAreaView
            style={{
                flex: 1,
                backgroundColor: getColor('$white'),
                display: 'flex',
                flexDirection: isNonMobileWeb ? 'row' : 'column',
                alignContent: isNonMobileWeb ? 'flex-end' : 'flex-start',
                justifyContent: isNonMobileWeb ? 'flex-end' : 'flex-start',
            }}
        >
            <View
                overflow="hidden"
                position="absolute"
                top={0}
                left={0}
                width={isNonMobileWeb ? '50%' : '100%'}
                height={mediaHeight}
                enterStyle={{ opacity: 0, scale: 0.98 }}
                exitStyle={{ opacity: 0, scale: 0.98 }}
                animateOnly={['opacity', 'transform', 'scale']}
            >
                <ImageBackground
                    source={backgroundImage}
                    resizeMode="cover"
                    style={{
                        height: '100%',
                        width: '100%',
                        overflow: 'hidden',
                    }}
                />
            </View>
            <ScrollView
                testID={scrollViewTestID}
                width={isNonMobileWeb ? '50%' : '100%'}
                minWidth={isNonMobileWeb ? 500 : undefined}
                flexGrow={isNonMobileWeb ? 0 : 1}
            >
                <YStack
                    flex={1}
                    backgroundColor="$white"
                    width="100%"
                    height="100%"
                    alignItems="center"
                    paddingVertical={isNonMobileWeb ? '5vh' : 32}
                    paddingHorizontal={isNonMobileWeb ? 32 : 16}
                    borderTopLeftRadius={isNonMobileWeb ? 0 : 24}
                    borderTopRightRadius={isNonMobileWeb ? 0 : 24}
                    marginTop={contentTopMargin}
                    style={
                        isWeb && !isNonMobileWeb
                            ? {
                                  boxShadow: '0 -6px 12px rgba(0, 0, 0, 0.15)',
                              }
                            : undefined
                    }
                >
                    <YStack
                        width="100%"
                        maxWidth={550}
                        $lg={{ paddingVertical: '10vh' }}
                        animation="quick"
                        enterStyle={{
                            opacity: 0,
                            scale: 0.98,
                            translateY: isNonMobileWeb ? 4 : 16,
                        }}
                        animateOnly={[
                            'opacity',
                            'transform',
                            'scale',
                            'translateY',
                        ]}
                    >
                        <FormHeader
                            title={title}
                            subtitle={subtitle}
                            includeLogo={includeLogo}
                        />
                        {children}
                    </YStack>
                    {disclosures}
                </YStack>
            </ScrollView>
        </SafeAreaView>
    );
};

const FormHeader = ({
    title,
    subtitle,
    includeLogo = false,
}: {
    title: string;
    subtitle: string;
    includeLogo?: boolean;
}) => {
    const { getColor } = useThemeColors();

    return (
        <YStack maxWidth={500} alignSelf="center">
            {includeLogo && (
                <View marginBottom={12} alignSelf="center">
                    <Logo height={30} color={getColor('$green1000')} />
                </View>
            )}
            {isNonMobileWeb ? (
                <>
                    <HeroEmphasized
                        textAlign="center"
                        marginVertical={isNonMobileWeb ? 32 : 16}
                    >
                        {title}
                    </HeroEmphasized>
                    <BodyLargeRegular
                        textAlign="center"
                        alignSelf="center"
                        marginBottom={32}
                    >
                        {subtitle}
                    </BodyLargeRegular>
                </>
            ) : (
                <>
                    <H1Emphasized
                        textAlign="center"
                        marginVertical={isNonMobileWeb ? 32 : 16}
                    >
                        {title}
                    </H1Emphasized>
                    <BodyMediumRegular
                        maxWidth={280}
                        textAlign="center"
                        alignSelf="center"
                        marginBottom={32}
                    >
                        {subtitle}
                    </BodyMediumRegular>
                </>
            )}
        </YStack>
    );
};
